/* eslint-disable id-length */
import type { JSX, CSSProperties, MouseEvent as ReactMouseEvent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { ScreenReaderOnly } from '../ScreenReaderOnly';
import { useColorPickerState } from './ColorPickerContext';
import { HSVSaturationValueCanvas } from './HSVSaturationValueCanvas';
import { SV_AREA_CANVAS_WIDTH_PX, SV_AREA_CANVAS_HEIGHT_PX } from './consts';
import { convertToHsv } from './color-conversion-utils';

const SATURATION_NUDGE = 0.02;
const VALUE_NUDGE = 0.02;

const Container = styled.div`
  border-radius: var(--wui-border-radius-02);
  box-shadow: 0 0 0 1px var(--wui-color-border);
  overflow: hidden;
  position: relative;
`;

const Thumb = styled.button.attrs({ type: 'button' })`
  appearance: none;
  border-radius: var(--wui-border-radius-rounded);
  border: none;
  box-shadow:
    inset 0 0 0 1px var(--wui-color-border),
    inset 0 0 0 2px black;
  cursor: pointer;
  height: 16px;
  outline: none;
  position: absolute;
  transform: translate(-50%, -50%);
  width: 16px;

  &:focus-visible {
    box-shadow:
      inset 0 0 0 2px var(--wui-color-border),
      inset 0 0 0 3px black;
  }
`;

export type SaturationAndValuePickerProps = {
  dataTestId?: string;
};

/**
 * A 2D picking area for adjusting saturation (x-axis) and brightness/value
 * (y-axis) of the current color. Supports click, drag, and keyboard arrow
 * navigation. When contrast enforcement is enabled, a contrast boundary curve
 * is drawn on the canvas.
 */
export const SaturationAndValuePicker = ({
  dataTestId,
}: SaturationAndValuePickerProps): JSX.Element => {
  const {
    colorForComparison,
    isDragging,
    isTransitioning,
    minimumContrastRatio,
    nonDerivedValueAsHsv,
    onChangeNonDerivedValueAsHsv,
    opacityForContrastCalculation,
    setIsDragging,
    shouldEnforceMinContrast,
    valueAsHex,
    valueAsHsv,
  } = useColorPickerState();
  const containerRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLButtonElement>(null);
  const uuid = useMemo(() => crypto.randomUUID(), []);
  const instructionsId = `sv-instructions-${uuid}`;

  const valueAsHexConvertedToHsv = convertToHsv(valueAsHex);

  const thumbPositionHsv = useMemo(() => {
    // Some precision is lost when converting HSV to hex and back to HSV. When
    // dragging around (which happens when _not_ transitioning, as transitioning
    // only happens for a moment upon toggling contrast enforcement), we want
    // the thumb position to be very precise. BUT valueAsHsv doesn't get updated
    // during transitions, so to transition the y position of the thumb we must
    // use valueAsHexConvertedToHsv then.
    if (!isTransitioning) {
      return valueAsHsv;
    }
    return valueAsHexConvertedToHsv;
  }, [isTransitioning, valueAsHexConvertedToHsv, valueAsHsv]);

  const [thumbStyle, setThumbStyle] = useState<CSSProperties>({
    backgroundColor: valueAsHex,
  });

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const thumbX = thumbPositionHsv.s * (container.clientWidth || SV_AREA_CANVAS_WIDTH_PX);
    const thumbY = (1 - thumbPositionHsv.v) * (container.clientHeight || SV_AREA_CANVAS_HEIGHT_PX);

    setThumbStyle({
      left: `${thumbX}px`,
      top: `${thumbY}px`,
      backgroundColor: valueAsHex,
    });
  }, [thumbPositionHsv.s, thumbPositionHsv.v, valueAsHex]);

  const nudgeHsv = useCallback(
    (channel: 's' | 'v', delta: number) => {
      if (channel === 's') {
        const newSaturation = Math.max(0, Math.min(1, nonDerivedValueAsHsv.s + delta));

        onChangeNonDerivedValueAsHsv({
          ...nonDerivedValueAsHsv,
          s: newSaturation,
        });
        return;
      }

      // channel must === 'v'
      const newValue = Math.max(0, Math.min(1, nonDerivedValueAsHsv.v + delta));

      onChangeNonDerivedValueAsHsv({
        ...nonDerivedValueAsHsv,
        v: newValue,
      });
    },
    [nonDerivedValueAsHsv, onChangeNonDerivedValueAsHsv],
  );

  const updateSaturationAndValueFromCoordinates = useCallback(
    (clientX: number, clientY: number) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));

      const saturation = x;
      const value = 1 - y;

      onChangeNonDerivedValueAsHsv({
        ...nonDerivedValueAsHsv,
        s: saturation,
        v: value,
      });
    },
    [onChangeNonDerivedValueAsHsv, nonDerivedValueAsHsv],
  );

  const onContainerMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      thumbRef.current?.focus();
      setIsDragging(true);
      updateSaturationAndValueFromCoordinates(event.clientX, event.clientY);
    },
    [setIsDragging, updateSaturationAndValueFromCoordinates],
  );

  const onThumbMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      thumbRef.current?.focus();
      setIsDragging(true);
    },
    [setIsDragging],
  );

  const onThumbKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        nudgeHsv('s', SATURATION_NUDGE);
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        nudgeHsv('s', -SATURATION_NUDGE);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        nudgeHsv('v', VALUE_NUDGE);
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        nudgeHsv('v', -VALUE_NUDGE);
      }
    },
    [nudgeHsv],
  );

  const onWindowMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging) {
        return;
      }
      updateSaturationAndValueFromCoordinates(event.clientX, event.clientY);
    },
    [isDragging, updateSaturationAndValueFromCoordinates],
  );

  const onWindowMouseUp = useCallback(() => setIsDragging(false), [setIsDragging]);

  useEffect(() => {
    window.addEventListener('mousemove', onWindowMouseMove);
    window.addEventListener('mouseup', onWindowMouseUp);

    return () => {
      window.removeEventListener('mousemove', onWindowMouseMove);
      window.removeEventListener('mouseup', onWindowMouseUp);
    };
  }, [onWindowMouseMove, onWindowMouseUp]);

  return (
    // eslint-disable-next-line styled-components-a11y/no-static-element-interactions
    <Container
      ref={containerRef}
      data-testid={dataTestId}
      onMouseDown={onContainerMouseDown}
    >
      <Thumb
        ref={thumbRef}
        aria-describedby={instructionsId}
        aria-label="Saturation and value thumb"
        onKeyDown={onThumbKeyDown}
        onMouseDown={onThumbMouseDown}
        style={thumbStyle}
        tabIndex={0}
      />
      <HSVSaturationValueCanvas
        colorForComparison={colorForComparison}
        hue={nonDerivedValueAsHsv.h ?? 0}
        minimumContrastRatio={minimumContrastRatio}
        opacityForContrastCalculation={opacityForContrastCalculation}
        shouldShowContrastCurve={shouldEnforceMinContrast}
      />
      <ScreenReaderOnly id={instructionsId}>
        Move right to increase saturation, left to decrease saturation, up to increase value, and
        down to decrease value.
      </ScreenReaderOnly>
      <ScreenReaderOnly aria-live="polite">
        Current color: {valueAsHex}. Hue: {(nonDerivedValueAsHsv.h ?? 0).toFixed(2)}, Saturation:{' '}
        {nonDerivedValueAsHsv.s.toFixed(2)}, Value: {nonDerivedValueAsHsv.v.toFixed(2)}.
      </ScreenReaderOnly>
    </Container>
  );
};

SaturationAndValuePicker.displayName = 'SaturationAndValuePicker';
